import React from 'react';
import { useMoney } from '../../hooks/useMoney';

/**
 * Consistent price display used across bottom bars, cards, modals.
 *
 * @param {string} label - e.g. '예상 결제액'
 * @param {number|string} amount - numeric amount or pre-formatted string
 * @param {string} size - 'sm' | 'md' (default) | 'lg'
 * @param {number|string} originalAmount - struck-through original price (coupon)
 * @param {string} badge - optional badge text (e.g. '쿠폰 적용')
 * @param {string} layout - 'inline' (default, label+price side by side) | 'stack' (label above price)
 */
export default function PriceDisplay({
  label,
  amount,
  size = 'md',
  originalAmount,
  badge,
  layout = 'inline',
  approx = false,
}) {
  const money = useMoney();
  const formatted = money(amount);
  const formattedOriginal = (originalAmount != null && originalAmount !== '') ? money(originalAmount) : null;
  // #272: 예상 결제액이 USD(환율 환산값)일 때만 근사 표시(~) — 환산이라 정확히 일치하지 않음을 안내.
  //   원화(KRW)는 실제 청구액과 동일하므로 미표시. (차량정보 모달과 동일 의미)
  const tilde = approx && /^US\$/.test(String(formatted)) ? ' ~' : '';

  const sizeMap = {
    sm: { price: 'text-lg sm:text-xl', label: 'text-2sm sm:text-sm', struck: 'text-2sm sm:text-sm' },
    md: { price: 'text-xl sm:text-2xl', label: 'text-sm', struck: 'text-2sm sm:text-sm' },
    lg: { price: 'text-2xl sm:text-3xl', label: 'text-sm sm:text-base', struck: 'text-sm' },
  };
  const s = sizeMap[size] || sizeMap.md;

  const isStack = layout === 'stack';

  return (
    <div className={`flex flex-col ${isStack ? '' : 'items-start gap-1'}`}>
      <div className="flex items-center gap-2">
        {label && <span className={`${s.label} font-medium text-mono-500`}>{label}</span>}
        {badge && (
          <span className="inline-flex items-center px-2 py-1 bg-orange-50 rounded-badge text-2xs font-bold text-orange-500">
            {badge}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {formattedOriginal && (
          <span className={`${s.struck} font-medium text-mono-400 line-through`}>{formattedOriginal}</span>
        )}
        <span className={`${s.price} font-bold text-mono-900`}>{formatted}{tilde}</span>
      </div>
    </div>
  );
}
